/**
 * PICalculator Component
 * Main component for the Promoting Interoperability calculator
 * Split-pane layout with data entry on left and score summary on right
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Accordion } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Users, User, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PIScoreSummary } from './PIScoreSummary';
import { PIModeledBanner } from './PIModeledBanner';
import { PIRequirementsBanner } from './PIRequirementsBanner';
import { PINpiBulkUpload } from './PINpiBulkUpload';
// PIBulkEditor is available but hidden for now - can be re-enabled later
// import { PIBulkEditor } from './PIBulkEditor';
import {
  ReportingContextSection,
  PrerequisitesSection,
  EPrescribingSection,
  PatientAccessSection,
  HIESection,
  PublicHealthSection,
} from './PISections';
import {
  getPICalculatorData,
  savePICalculatorData,
  calculatePIScoreClient,
  type PICalculatorData,
  type PICalculatedScore,
} from '@/lib/pi-calculator-utils';
import { getProviders, type Provider } from '@/lib/measure-outcomes';
import { useProgramYear } from '@/contexts/ProgramYearContext';

interface PICalculatorProps {
  practiceId: string;
  practiceName?: string;
  useModeledPi: boolean;
  modeledPiPercent: number;
  piWeight: number;
  onToggleModeled: (useModeled: boolean) => void;
  /** Callback with raw percentage score (0-100). Parent applies weights. */
  onScoreChange?: (rawScore: number) => void;
  providers?: Provider[];
  evaluationType: 'GROUP' | 'NPI';
  selectedNpi?: string;
  onEvaluationTypeChange?: (type: 'GROUP' | 'NPI') => void;
  onNpiChange?: (npi: string) => void;
  /** When true (e.g. locked year), all inputs are read-only and autosave is disabled. */
  disabled?: boolean;
}

export function PICalculator({
  practiceId,
  practiceName,
  useModeledPi,
  modeledPiPercent,
  piWeight,
  onToggleModeled,
  onScoreChange,
  providers: externalProviders,
  evaluationType,
  selectedNpi,
  onEvaluationTypeChange,
  onNpiChange,
  disabled = false,
}: PICalculatorProps) {
  const { programYear } = useProgramYear();
  // State
  const [data, setData] = useState<PICalculatorData>({});
  const [score, setScore] = useState<PICalculatedScore>(calculatePIScoreClient({}));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>(externalProviders || []);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // Auto-save debounce ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Ref to always have the latest autoSave function (avoids stale closure issues)
  const autoSaveRef = useRef<(dataToSave: PICalculatorData) => Promise<void>>();
  
  // Ref to store onScoreChange callback to avoid stale closures and infinite loops
  const onScoreChangeRef = useRef(onScoreChange);
  useEffect(() => {
    onScoreChangeRef.current = onScoreChange;
  }, [onScoreChange]);

  // Load providers if not provided externally
  useEffect(() => {
    if (!externalProviders && evaluationType === 'NPI') {
      setLoadingProviders(true);
      getProviders(practiceId).then((p) => {
        setProviders(p);
        setLoadingProviders(false);
      });
    } else if (externalProviders) {
      setProviders(externalProviders);
    }
  }, [practiceId, externalProviders, evaluationType]);

  // Track initial load vs. subsequent updates
  const hasLoadedRef = useRef(false);

  // Load data when practice/NPI changes
  useEffect(() => {
    const loadData = async () => {
      // Only show full skeleton on initial load, not when switching between modes
      if (!hasLoadedRef.current) {
        setIsLoading(true);
      }
      try {
        const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
        const result = await getPICalculatorData(practiceId, npi, programYear);
        
        let loadedScore: PICalculatedScore;
        if (result) {
          // Use existing data or empty object
          const loadedData = result.data || result;
          setData(loadedData);
          loadedScore = result.calculatedScore || calculatePIScoreClient(loadedData);
          setScore(loadedScore);
        } else {
          // null means the fetch failed (the API returns a 200 + empty shell
          // when no data exists) -- surface it instead of showing "no data"
          setSaveError('Failed to load PI data. Please refresh the page.');
          setData({});
          loadedScore = calculatePIScoreClient({});
          setScore(loadedScore);
        }
        
        // Notify parent of raw score (only if not using modeled) - parent applies weights
        if (!useModeledPi && onScoreChangeRef.current) {
          onScoreChangeRef.current(loadedScore.totalScore);
        }
      } catch (error) {
        console.error('Error loading PI data:', error);
        setSaveError('Failed to load PI data. Please refresh the page.');
        setData({});
        const emptyScore = calculatePIScoreClient({});
        setScore(emptyScore);
        
        // Notify parent of empty score - parent applies weights
        if (!useModeledPi && onScoreChangeRef.current) {
          onScoreChangeRef.current(emptyScore.totalScore);
        }
      } finally {
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    };

    loadData();
  }, [practiceId, evaluationType, selectedNpi, useModeledPi, programYear]);

  // Auto-save function
  const autoSave = useCallback(async (dataToSave: PICalculatorData) => {
    if (useModeledPi || disabled) return; // Don't save if using modeled scores or year is locked
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
      const saveData = { ...dataToSave, npi };
      const result = await savePICalculatorData(practiceId, saveData, programYear);
      
      // Only set lastSaved if the save was actually successful
      if (result) {
        setLastSaved(new Date());
      } else {
        setSaveError('Failed to save. Please try again.');
      }
    } catch (error) {
      console.error('Error saving PI data:', error);
      setSaveError('Failed to save. Changes may be lost.');
    } finally {
      setIsSaving(false);
    }
  }, [practiceId, evaluationType, selectedNpi, useModeledPi, programYear, disabled]);

  // Keep autoSaveRef updated with the latest autoSave function
  useEffect(() => {
    autoSaveRef.current = autoSave;
  }, [autoSave]);

  // Handle data changes with debounced auto-save
  const handleDataChange = useCallback((updates: Partial<PICalculatorData>) => {
    setData((prev) => {
      const newData = { ...prev, ...updates };
      const newScore = calculatePIScoreClient(newData);
      setScore(newScore);
      
      // Notify parent of raw score change - parent applies weights
      onScoreChangeRef.current?.(newScore.totalScore);
      
      // Debounced auto-save using ref to get latest function (avoids stale closure)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        autoSaveRef.current?.(newData);
      }, 1000); // 1 second debounce
      
      return newData;
    });
  }, []); // No dependencies - raw score doesn't need weight

  // Handle modeled toggle
  const handleToggleModeled = async (useModeled: boolean) => {
    onToggleModeled(useModeled);
    
    // If switching to calculator, trigger a save of current data
    if (!useModeled && Object.keys(data).length > 0) {
      await autoSave(data);
    }
  };

  // Handle bulk upload completion
  const handleBulkUploadComplete = () => {
    // Reload data after bulk upload (same viewing year the upload wrote to)
    const loadData = async () => {
      const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
      const result = await getPICalculatorData(practiceId, npi, programYear);
      if (result) {
        const loadedData = result.data || result;
        setData(loadedData);
        setScore(result.calculatedScore || calculatePIScoreClient(loadedData));
      }
    };
    loadData();
  };

  // Handle reset all
  const handleReset = useCallback(() => {
    if (disabled) return;
    if (window.confirm('Are you sure you want to reset all PI calculator inputs? This action cannot be undone.')) {
      const emptyData: PICalculatorData = {};
      setData(emptyData);
      setScore(calculatePIScoreClient(emptyData));
      // Save the empty state
      autoSave(emptyData);
    }
  }, [autoSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Modeled Score Banner */}
      <PIModeledBanner
        useModeledPi={useModeledPi}
        modeledPiPercent={modeledPiPercent}
        onToggleModeled={handleToggleModeled}
        isUpdating={isSaving}
        disabled={disabled}
      />

      {/* Evaluation Type Toggle & NPI Selection (for NPI mode) */}
      {evaluationType === 'NPI' && (
        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant={evaluationType === 'GROUP' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onEvaluationTypeChange?.('GROUP')}
              >
                <Users className="w-4 h-4 mr-2" />
                Group/TIN
              </Button>
              <Button
                variant={evaluationType === 'NPI' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onEvaluationTypeChange?.('NPI')}
              >
                <User className="w-4 h-4 mr-2" />
                NPI
              </Button>
            </div>

            <Select
              value={selectedNpi || ''}
              onValueChange={(v) => onNpiChange?.(v)}
              disabled={loadingProviders}
            >
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.npi} value={p.npi}>
                    {p.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Upload Button */}
          <PINpiBulkUpload
            practiceId={practiceId}
            year={programYear}
            onUploadComplete={handleBulkUploadComplete}
            disabled={disabled}
          />
        </div>
      )}

      {/* When modeled, show only the disabled message */}
      {useModeledPi ? (
        <Alert className="bg-muted/50">
          <AlertDescription>
            The PI Calculator is disabled while using modeled scores. 
            Switch to the calculator to enter actual PI measure data.
          </AlertDescription>
        </Alert>
      ) : (
        /* Main Content - Split Pane Layout (only when calculator is active) */
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          {/* Left Pane - Scrollable Form (60% on large screens) */}
          <div className="flex-1 lg:flex-[3] space-y-4">
            {/* Save Status */}
            <div className="flex items-center justify-end gap-2 text-sm">
              {isSaving ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Saving...</span>
                </>
              ) : saveError ? (
                <>
                  <AlertTriangle className="w-3 h-3 text-destructive" />
                  <span className="text-destructive">{saveError}</span>
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  <span className="text-muted-foreground">Saved {lastSaved.toLocaleTimeString()}</span>
                </>
              ) : null}
            </div>

            {/* Requirements Guidance Banner */}
            <PIRequirementsBanner data={data} score={score} />

            {/* Accordion Sections */}
            <Accordion type="multiple" className="space-y-3" defaultValue={[]}>
              <ReportingContextSection
                data={data}
                onChange={handleDataChange}
                disabled={useModeledPi || disabled}
              />
              
              <PrerequisitesSection
                data={data}
                onChange={handleDataChange}
                disabled={useModeledPi || disabled}
              />
              
              <EPrescribingSection
                data={data}
                onChange={handleDataChange}
                disabled={useModeledPi || disabled}
                breakdown={score.breakdown.ePrescribing}
                queryPdmpBreakdown={score.breakdown.queryPdmp}
              />
              
              <PatientAccessSection
                data={data}
                onChange={handleDataChange}
                disabled={useModeledPi || disabled}
                breakdown={score.breakdown.patientAccess}
              />
              
              <HIESection
                data={data}
                onChange={handleDataChange}
                disabled={useModeledPi || disabled}
                breakdown={score.breakdown.hie}
              />
              
              <PublicHealthSection
                data={data}
                onChange={handleDataChange}
                disabled={useModeledPi || disabled}
                breakdown={score.breakdown.publicHealth}
                bonusBreakdown={score.breakdown.bonus}
              />
            </Accordion>
          </div>

          {/* Right Pane - Score Panel (40% on large screens, expands with screen) */}
          <div className="lg:flex-[2] lg:min-w-[320px] lg:max-w-[480px]">
            {/* Spacer to align with left pane: matches save status height + space-y-4 gap */}
            <div className={(isSaving || saveError || lastSaved) ? "h-9" : "h-4"} />
            <PIScoreSummary score={score} piWeight={piWeight} onReset={handleReset} disabled={disabled} />
          </div>
        </div>
      )}
    </div>
  );
}

// Export index file content
export { PIScoreSummary } from './PIScoreSummary';
export { PIModeledBanner } from './PIModeledBanner';
export { PIRequirementsBanner } from './PIRequirementsBanner';
export { PINpiBulkUpload } from './PINpiBulkUpload';
export * from './PISections';

